import React, { useRef, useState } from 'react';
import { uploadAndUpdateAvatar } from 'services/profile.service';
import { useAuth } from 'context/AuthContext';
import fotoPadraoBB from 'assets/img/2026/new_foto_padrao_bb.png';
import bordaEuParticipo from 'assets/img/2026/borda_eu_participo.png';
import seloEuVotei from 'assets/img/2026/selo_eu_votei.png';
import './Hero.scss';

interface HeroProps {
  user: {
    nome: string;
    foto: string;
  };
  badges: Array<{
    id: number;
    nome: string;
    imagem: string;
    conquistado: boolean;
  }>;
  isParticipanteConfirmado: boolean;
  hasConquistaVotar?: boolean;
  ilustracao: string;
  onOpenOnboarding?: () => void;
  onViewMoreBadges?: () => void;
}

const loadImage = (source: string, crossOrigin?: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (crossOrigin) {
      image.crossOrigin = crossOrigin;
    }

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Falha ao carregar imagem para composição.'));
    image.src = source;
  });
};

const DOWNLOAD_AVATAR_SCALE_X = 1;
const DOWNLOAD_AVATAR_SCALE_Y = 1;
const DOWNLOAD_AVATAR_OFFSET_X = 0;
const DOWNLOAD_AVATAR_OFFSET_Y = 0;
const DOWNLOAD_FRAME_SCALE_X = 1.5;
const DOWNLOAD_FRAME_SCALE_Y = 1.24;
const DOWNLOAD_FRAME_OFFSET_X = 0;
const DOWNLOAD_FRAME_OFFSET_Y = -0.10;
const DOWNLOAD_OUTPUT_MAX_SIDE = 2048;
const DOWNLOAD_EXPORT_PADDING_RATIO = 0.08;

export const Hero: React.FC<HeroProps> = ({
  user,
  badges,
  isParticipanteConfirmado,
  hasConquistaVotar = false,
  ilustracao,
  onOpenOnboarding,
  onViewMoreBadges
}) => {
  const { user: authUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingFramedPhoto, setIsDownloadingFramedPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const avatarVisualRef = useRef<HTMLDivElement>(null);
  const avatarContainerRef = useRef<HTMLDivElement>(null);
  const avatarImageRef = useRef<HTMLImageElement>(null);
  const avatarFrameRef = useRef<HTMLImageElement>(null);
  const conquistedBadges = badges.filter(b => b.conquistado).slice(0, 3);
  const avatarSrc = user.foto || fotoPadraoBB;
  const showFrame = hasConquistaVotar || isParticipanteConfirmado;
  const frameSrc = hasConquistaVotar ? seloEuVotei : bordaEuParticipo;
  const frameAlt = hasConquistaVotar ? 'Selo Eu votei' : 'Borda Eu participo';
  const frameDownloadSuffix = hasConquistaVotar ? 'eu-votei' : 'eu-participo';

  const handleEditPhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !authUser?._id) return;

    // Valida tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    // Valida tamanho do arquivo (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB em bytes
    if (file.size > maxSize) {
      alert('A imagem deve ter no máximo 5MB.');
      return;
    }

    try {
      setIsUploading(true);

      // Usa a função uploadAndUpdateAvatar que já faz tudo
      await uploadAndUpdateAvatar(authUser._id, file);
      
      // Recarrega a página para exibir a nova foto
      window.location.reload();
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      alert('Erro ao atualizar foto de perfil. Tente novamente.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadPhotoWithFrame = async () => {
    if (!showFrame || isUploading) {
      return;
    }

    try {
      setIsDownloadingFramedPhoto(true);

      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Não foi possível preparar a imagem para download.');
      }

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      const avatarContainerElement = avatarContainerRef.current;
      const avatarImageElement = avatarImageRef.current;
      const avatarFrameElement = avatarFrameRef.current;

      if (!avatarContainerElement || !avatarImageElement || !avatarFrameElement) {
        throw new Error('Não foi possível obter a referência visual da moldura.');
      }

      const avatarContainerRect = avatarContainerElement.getBoundingClientRect();

      const [avatarImage, frameImage] = await Promise.all([
        loadImage(avatarImageElement.currentSrc || avatarSrc, 'anonymous'),
        loadImage(avatarFrameElement.currentSrc || frameSrc)
      ]);

      const avatarRect = {
        x: 0,
        y: 0,
        width: Math.max(avatarContainerRect.width, 1),
        height: Math.max(avatarContainerRect.height, 1),
      };

      const tunedAvatarRect = {
        x:
          avatarRect.x +
          (avatarRect.width * (1 - DOWNLOAD_AVATAR_SCALE_X)) / 2 +
          avatarRect.width * DOWNLOAD_AVATAR_OFFSET_X,
        y:
          avatarRect.y +
          (avatarRect.height * (1 - DOWNLOAD_AVATAR_SCALE_Y)) / 2 +
          avatarRect.height * DOWNLOAD_AVATAR_OFFSET_Y,
        width: avatarRect.width * DOWNLOAD_AVATAR_SCALE_X,
        height: avatarRect.height * DOWNLOAD_AVATAR_SCALE_Y,
      };

      const tunedFrameRect = {
        x:
          tunedAvatarRect.x +
          (tunedAvatarRect.width * (1 - DOWNLOAD_FRAME_SCALE_X)) / 2 +
          tunedAvatarRect.width * DOWNLOAD_FRAME_OFFSET_X,
        y:
          tunedAvatarRect.y +
          (tunedAvatarRect.height * (1 - DOWNLOAD_FRAME_SCALE_Y)) / 2 +
          tunedAvatarRect.height * DOWNLOAD_FRAME_OFFSET_Y,
        width: tunedAvatarRect.width * DOWNLOAD_FRAME_SCALE_X,
        height: tunedAvatarRect.height * DOWNLOAD_FRAME_SCALE_Y,
      };

      const contentLeft = Math.min(tunedAvatarRect.x, tunedFrameRect.x);
      const contentTop = Math.min(tunedAvatarRect.y, tunedFrameRect.y);
      const contentRight = Math.max(
        tunedAvatarRect.x + tunedAvatarRect.width,
        tunedFrameRect.x + tunedFrameRect.width
      );
      const contentBottom = Math.max(
        tunedAvatarRect.y + tunedAvatarRect.height,
        tunedFrameRect.y + tunedFrameRect.height
      );

      const contentWidth = Math.max(contentRight - contentLeft, 1);
      const contentHeight = Math.max(contentBottom - contentTop, 1);
      const padding = Math.max(contentWidth, contentHeight) * DOWNLOAD_EXPORT_PADDING_RATIO;

      const exportWidth = contentWidth + padding * 2;
      const exportHeight = contentHeight + padding * 2;
      const exportScale = DOWNLOAD_OUTPUT_MAX_SIDE / Math.max(exportWidth, exportHeight);

      canvas.width = Math.max(Math.round(exportWidth * exportScale), 1);
      canvas.height = Math.max(Math.round(exportHeight * exportScale), 1);

      const toCanvasRect = (rect: { x: number; y: number; width: number; height: number }) => ({
        x: (rect.x - contentLeft + padding) * exportScale,
        y: (rect.y - contentTop + padding) * exportScale,
        width: rect.width * exportScale,
        height: rect.height * exportScale,
      });

      const avatarCanvasRect = toCanvasRect(tunedAvatarRect);
      const frameCanvasRect = toCanvasRect(tunedFrameRect);

      context.save();
      context.beginPath();
      context.arc(
        avatarCanvasRect.x + avatarCanvasRect.width / 2,
        avatarCanvasRect.y + avatarCanvasRect.height / 2,
        Math.min(avatarCanvasRect.width, avatarCanvasRect.height) / 2,
        0,
        Math.PI * 2
      );
      context.closePath();
      context.clip();

      const avatarScale = Math.max(
        avatarCanvasRect.width / avatarImage.width,
        avatarCanvasRect.height / avatarImage.height
      );
      const avatarWidth = avatarImage.width * avatarScale;
      const avatarHeight = avatarImage.height * avatarScale;
      const avatarX = avatarCanvasRect.x + (avatarCanvasRect.width - avatarWidth) / 2;
      const avatarY = avatarCanvasRect.y + (avatarCanvasRect.height - avatarHeight) / 2;

      context.drawImage(avatarImage, avatarX, avatarY, avatarWidth, avatarHeight);
      context.restore();

      context.drawImage(
        frameImage,
        frameCanvasRect.x,
        frameCanvasRect.y,
        frameCanvasRect.width,
        frameCanvasRect.height
      );

      const dataUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      const sanitizedName = user.nome
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') || 'usuario';

      downloadLink.href = dataUrl;
      downloadLink.download = `foto-${sanitizedName}-${frameDownloadSuffix}.png`;
      downloadLink.click();
    } catch (error) {
      console.error('Erro ao gerar foto com borda:', error);
      alert('Não foi possível gerar sua foto com borda. Verifique se a imagem de perfil está acessível e tente novamente.');
    } finally {
      setIsDownloadingFramedPhoto(false);
    }
  };

  const isDownloadButtonDisabled = !showFrame || isUploading || isDownloadingFramedPhoto;
  const downloadButtonLabel = isDownloadingFramedPhoto
    ? 'Gerando imagem...'
    : 'Baixar selo';

  return (
    <div className="hero">
      <div className="hero__content">
        <div className="hero__user-section">
          <div className="hero__avatar-container">
            <div className="hero__avatar-visual" ref={avatarVisualRef}>
              <div className="hero__avatar" ref={avatarContainerRef}>
                <img
                  ref={avatarImageRef}
                  src={avatarSrc}
                  alt={`Foto de perfil de ${user.nome}`}
                  tabIndex={0}
                  onError={(event) => {
                    if (event.currentTarget.src !== fotoPadraoBB) {
                      event.currentTarget.src = fotoPadraoBB;
                    }
                  }}
                />
              </div>
              {showFrame && (
                <img
                  ref={avatarFrameRef}
                  src={frameSrc}
                  alt={frameAlt}
                  className="hero__avatar-frame"
                />
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              aria-label="Selecionar imagem de perfil"
            />
            <div className="hero__avatar-actions">
              <button
                className="hero__edit-photo"
                onClick={handleEditPhotoClick}
                disabled={isUploading || isDownloadingFramedPhoto}
                aria-label={isUploading ? 'Enviando foto de perfil...' : 'Editar foto de perfil'}
                aria-busy={isUploading}
                style={{
                  opacity: isUploading || isDownloadingFramedPhoto ? 0.6 : 1,
                  cursor: isUploading || isDownloadingFramedPhoto ? 'not-allowed' : 'pointer'
                }}
              >
                {isUploading ? 'Enviando...' : 'Editar'}
              </button>

              <button
                className="hero__download-framed-photo"
                onClick={handleDownloadPhotoWithFrame}
                disabled={isDownloadButtonDisabled}
                aria-label={downloadButtonLabel}
                aria-busy={isDownloadingFramedPhoto}
                title={!showFrame ? 'Disponível para usuários confirmados em iniciativas ou que votaram nas iniciativas' : undefined}
              >
                {downloadButtonLabel}
              </button>
            </div>
          </div>

          <div className="hero__content-wrapper">
            <div className="hero__text">
              <h1 className="hero__greeting" tabIndex={0}>
                Olá, <span className="hero__name">{user.nome}</span>
              </h1>
              <p className="hero__subtitle" tabIndex={0}>
                Bem-vindo(a) à sua jornada<br />
                no <span className="hero__highlight">Prêmio BB 2027</span>
              </p>
            </div>

            {conquistedBadges.length > 0 && (
              <div className="hero__badges" role="list" aria-label="Badges conquistados recentes">
                {conquistedBadges.map((badge) => (
                  <div
                    key={badge.id}
                    className="hero__badge"
                    role="listitem"
                    tabIndex={0}
                    aria-label={`Badge: ${badge.nome}`}
                  >
                    <img src={badge.imagem} alt="" aria-hidden="true" />
                  </div>
                ))}
                <button type="button" className="hero__ver-badges" onClick={onViewMoreBadges}>
                  ver mais badges
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="hero__illustration">
          <div className="hero__illustration-container">
            <img 
              src={ilustracao} 
              alt="Ilustração espacial do Prêmio BB"
              className="hero__main-image"
              tabIndex={0}
            />
            
            {/* Elementos decorativos flutuantes */}
            <div className="hero__float-element hero__float-element--1" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="15" fill="#FDD835" opacity="0.3"/>
                <circle cx="20" cy="20" r="10" fill="#FDD835" opacity="0.6"/>
                <circle cx="20" cy="20" r="5" fill="#FDD835"/>
              </svg>
            </div>

            <div className="hero__float-element hero__float-element--2" aria-hidden="true">
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
                <path 
                  d="M15 5L17 13L25 15L17 17L15 25L13 17L5 15L13 13L15 5Z" 
                  fill="#FFFFFF" 
                  opacity="0.8"
                />
              </svg>
            </div>

            <div className="hero__float-element hero__float-element--3" aria-hidden="true">
              <div className="hero__circle hero__circle--yellow"></div>
            </div>

            <div className="hero__float-element hero__float-element--4" aria-hidden="true">
              <div className="hero__circle hero__circle--white"></div>
            </div>

            <button className="hero__tutorial-button" onClick={onOpenOnboarding} aria-label="Abrir tutorial do Prêmio BB">
              VER TUTORIAL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};